<script setup lang="ts">
  import { computed } from "vue";
  import bg1 from "~/assets/images/aigc/bg-1.png";
  import bg2 from "~/assets/images/aigc/bg-2.png";

  interface FunctionItem {
    functionName: string;
    functionDesc: string;
  }

  interface CoreFunctionProps {
    title: string;
    description?: string;
    url: string;
    imagePlacement: "left" | "right";
    mediaType?: "image" | "video";
    bg: "white" | "blue";
    anchorID: string;
    btnEmit: (...args: any[]) => void;
    functionList: FunctionItem[];
  }

  const props = withDefaults(defineProps<CoreFunctionProps>(), {
    description: "",
    mediaType: "image",
  });

  const imageOrder = computed(() => {
    return props.imagePlacement === "left" ? "order:1" : "order:3";
  });
</script>

<template>
  <div class="core-function" :style="`background-image:url(${bg === 'white' ? bg1 : bg2})`">
    <h2 :id="anchorID" class="core-function-title">{{ title }}</h2>
    <p v-if="description" class="core-function-description">{{ description }}</p>
    <div v-else class="core-function-title-block"></div>
    <div class="core-function-box">
      <div class="core-function-box-left">
        <div v-for="(item, idx) of functionList" :key="idx" class="core-function-item">
          <span class="core-function-item-name">{{ item.functionName }}</span>
          <span class="core-function-item-desc">{{ item.functionDesc }}</span>
        </div>
        <a-button @click="() => btnEmit()">免费体验</a-button>
      </div>
      <div class="core-function-box-right" :style="imageOrder">
        <img v-if="mediaType === 'image'" class="core-function-box-image" :src="url" />
        <video
          v-else
          class="core-function-box-image"
          controls
          disablePictureInPicture
          controlslist="noplaybackrate nodownload"
          webkit-playsinline="true"
          playsinline="true"
          x5-video-player-type="h5-page">
          <source :src="url" type="video/mp4" />
        </video>
      </div>
    </div>
  </div>
</template>

<style scoped lang="less">
  .core-function {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    min-height: 1080px;
    padding: 0 27px;
    padding-top: 172px;
    padding-bottom: 139px;
    background-size: cover;

    .core-function-title {
      margin-bottom: 0;
      font-size: 40px;
      font-weight: 600;
      line-height: 47px;
      color: #333;
    }

    .core-function-description {
      margin-top: 14px;
      font-size: 24px;
      line-height: 28px;
      color: #5a5d66;
      text-align: center;
    }

    .core-function-title-block {
      width: 32px;
      height: 6px;
      margin-top: 7px;
      background: linear-gradient(90deg, #0a5cff 0%, #66f 100%);
    }

    .core-function-box {
      display: flex;
      flex-wrap: nowrap;
      gap: 80px;
      align-items: center;
      justify-content: center;
      margin-top: 72px;

      @media only screen and (max-width: 1024px) {
        flex-flow: column wrap;
      }

      .core-function-box-left {
        order: 2;
        max-width: 571px;

        .core-function-item {
          margin-bottom: 34px;
          text-align: justify;

          &:last-of-type {
            margin-bottom: 0;
          }

          span {
            font-size: 20px;
            line-height: 40px;
            color: #333;

            &:nth-of-type(1) {
              font-weight: bolder;
            }
          }
        }

        .ant-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 180px;
          height: 64px;
          margin-top: 48px;
          font-size: 20px;
          line-height: 23px;
          color: #fff;
          background: linear-gradient(90deg, #3849ff 0%, #66f 100%);
          border: none !important;
          border-radius: 4px;

          @media only screen and (max-width: 1024px) {
            margin-right: auto;
            margin-left: auto;
          }
        }
      }

      .core-function-box-right {
        width: 100%;
        max-width: 677px;
        max-height: 380px;
        aspect-ratio: 677 / 380;
        padding: 17px;
        background: #fff;
        border-radius: 17px;
        box-shadow: 0 4px 12px rgba(16, 62, 130, 16%);

        @media only screen and (max-width: 1280px) {
          order: 1 !important;
        }

        .core-function-box-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 8px;
        }
      }
    }
  }
</style>
